import React from 'react';
import { MappedAEMProperties } from '../../../../types';
import Button from '../../../common/Button/Button';

import styles from './DownloadSupportDocument.module.scss';
import { useViewContext } from '../../../../lib/context/ViewContext';
import useErrorMapping from '../../../../lib/hooks/useErrorMapping';
import { Role, useRole } from '../../../../lib/context/SessionContext';

export type DownloadSupportDocumentProps = {
  buttonLabel: string;
  pdfLink: string;
  registrationLink: string;
  facilityNameXPosition: number;
  facilityNameYPosition: number;
  facilityNameFontSize: number;
  eanXPosition: number;
  eanYPosition: number;
  eanFontSize: number;
  qrCodeXPosition: number;
  qrCodeYPosition: number;
  qrCodeSize: number;
} & MappedAEMProperties;

export const DownloadSupportDocument = ({
  isInEditor,
  buttonLabel,
  pdfLink,
  registrationLink,
  facilityNameXPosition,
  facilityNameYPosition,
  facilityNameFontSize,
  eanXPosition,
  eanYPosition,
  eanFontSize,
  qrCodeXPosition,
  qrCodeYPosition,
  qrCodeSize,
}: DownloadSupportDocumentProps) => {
  const role = useRole();
  const { triggerSnackbarError } = useErrorMapping();

  const view = useViewContext();
  const ean = view.app.user?.mainFacilityEan;
  const facilityName = view.app.user?.mainFacilityName;

  const download = (pdfBytes: Uint8Array) => {
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const fileName =
      pdfLink
        ?.replace(/[?#].*/, '')
        ?.split('/')
        ?.pop() ?? 'facility.pdf';
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  async function preparePdf() {
    const existingPdfBytes = await fetch(`${pdfLink}`).then(res => {
      if (!res.ok) {
        throw new Error(`${res.status} ${res.statusText}: Failed to fetch PDF ${pdfLink}`);
      }

      return res.arrayBuffer();
    });
    const [{ PDFDocument, StandardFonts }, QRCode] = await Promise.all([import('pdf-lib'), import('qrcode')]);

    const pdfDoc = await PDFDocument.load(existingPdfBytes);
    const font = pdfDoc.embedStandardFont(StandardFonts.Helvetica);

    const pages = pdfDoc.getPages();
    const firstPage = pages[0];
    const { width, height } = firstPage.getSize();
    const xCoord = (x: number) => x * (width / 100);
    const yCoord = (y: number, itemHeight: number) => height - y * (height / 100) - itemHeight;

    if (facilityName) {
      firstPage.drawText(facilityName, {
        x: xCoord(facilityNameXPosition),
        y: yCoord(facilityNameYPosition, facilityNameFontSize),
        size: facilityNameFontSize,
        font,
      });
    }

    if (ean) {
      firstPage.drawText(ean, {
        x: xCoord(eanXPosition),
        y: yCoord(eanYPosition, eanFontSize),
        size: eanFontSize,
        lineHeight: eanFontSize,
        font,
      });

      const eanLinkAnnotation = pdfDoc.context.obj({
        Type: 'Annot',
        Subtype: 'Link',
        Rect: [
          xCoord(eanXPosition),
          yCoord(eanYPosition, eanFontSize),
          xCoord(eanXPosition) + font.widthOfTextAtSize(String(ean), eanFontSize),
          yCoord(eanYPosition, eanFontSize) + font.heightAtSize(eanFontSize, { descender: false }),
        ],
        Border: [0, 0, 0],
        A: {
          Type: 'Action',
          S: 'URI',
          URI: registrationLink,
        },
      });
      const eanLinkAnnotationRef = pdfDoc.context.register(eanLinkAnnotation);
      firstPage.node.addAnnot(eanLinkAnnotationRef);
    }

    const qrCodeDataUrl = await QRCode.toDataURL(registrationLink, {
      margin: 1,
    });
    const qrCodeImage = await pdfDoc.embedPng(qrCodeDataUrl);
    firstPage.drawImage(qrCodeImage, {
      x: xCoord(qrCodeXPosition),
      y: yCoord(qrCodeYPosition, xCoord(qrCodeSize)),
      width: xCoord(qrCodeSize),
      height: xCoord(qrCodeSize),
    });

    const linkAnnotation = pdfDoc.context.obj({
      Type: 'Annot',
      Subtype: 'Link',
      Rect: [
        xCoord(qrCodeXPosition),
        yCoord(qrCodeYPosition, xCoord(qrCodeSize)),
        xCoord(qrCodeXPosition) + xCoord(qrCodeSize),
        yCoord(qrCodeYPosition, xCoord(qrCodeSize)) + xCoord(qrCodeSize),
      ],
      Border: [0, 0, 0],
      A: {
        Type: 'Action',
        S: 'URI',
        URI: registrationLink,
      },
    });
    const linkAnnotationRef = pdfDoc.context.register(linkAnnotation);
    firstPage.node.addAnnot(linkAnnotationRef);

    return pdfDoc.save();
  }

  async function modifyPdf() {
    if (isInEditor) {
      return;
    }

    try {
      const pdfBytes = await preparePdf();
      download(pdfBytes);
    } catch (e) {
      triggerSnackbarError(e as any);
    }
  }

  return role === Role.LEARNER_MANAGER || isInEditor ? (
    <div className={styles.downloadContainer}>
      <Button onClick={() => modifyPdf()}>{buttonLabel}</Button>
    </div>
  ) : null;
};

export default DownloadSupportDocument;
